import { useCallback, useEffect, useRef, useState } from 'react';
import { clearAccessToken, getAccessToken, setAccessToken } from './lib/authStore';
import type { AdminMe } from './lib/panelApi';
import { commentCounts, me } from './lib/panelApi';
import { AccountPage } from './features/account/AccountPage';
import { AdminActivityPage } from './features/activity/AdminActivityPage';
import { LoginView } from './features/auth/LoginView';
import { AdminsPage } from './features/admins/AdminsPage';
import { AddBookFlow } from './features/books/AddBookFlow';
import { BooksListPage } from './features/books/BooksListPage';
import { EditBookFlow } from './features/books/EditBookFlow';
import { CommentsPage } from './features/comments/CommentsPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { CategoriesPage } from './features/categories/CategoriesPage';
import { ReportsPage } from './features/reports/ReportsPage';
import { TaxonomyPage } from './features/taxonomy/TaxonomyPage';
import { UploadsPage } from './features/uploads/UploadsPage';
import { UsersPage } from './features/users/UsersPage';

type View =
  | { name: 'dashboard' }
  | { name: 'books' }
  | { name: 'books:add' }
  | { name: 'books:edit'; uuid: string }
  | { name: 'categories' }
  | { name: 'taxonomy' }
  | { name: 'uploads' }
  | { name: 'comments' }
  | { name: 'users' }
  | { name: 'admins' }
  | { name: 'reports' }
  | { name: 'activity' }
  | { name: 'account' };

export function App() {
  const [accessToken, setAccessTokenState] = useState<string | null>(() => getAccessToken());
  const [admin, setAdmin] = useState<AdminMe | null>(null);
  const [adminError, setAdminError] = useState<string | null>(null);

  const [view, setViewRaw] = useState<View>(() => viewFromHash(window.location.hash));
  const [navOpen, setNavOpen] = useState(false);
  const [pendingComments, setPendingComments] = useState(0);

  // Ref to prevent hashchange listener from re-setting view when we push hash ourselves
  const skipNextHashChange = useRef(false);

  /** Navigate: update state + push hash */
  const navigate = useCallback((v: View) => {
    setViewRaw(v);
    const hash = viewToHash(v);
    if (window.location.hash !== hash) {
      skipNextHashChange.current = true;
      window.location.hash = hash;
    }
  }, []);

  // Listen for browser back/forward
  useEffect(() => {
    function onHashChange() {
      if (skipNextHashChange.current) {
        skipNextHashChange.current = false;
        return;
      }
      setViewRaw(viewFromHash(window.location.hash));
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    setNavOpen(false);
  }, [view.name]);

  const refreshMe = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await me(accessToken);
      setAdmin(res);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Session expired';
      setAdminError(msg);
      clearAccessToken();
      setAccessTokenState(null);
      setAdmin(null);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      setAdmin(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setAdminError(null);
      try {
        const res = await me(accessToken);
        if (!cancelled) setAdmin(res);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Session expired';
        if (!cancelled) {
          setAdminError(msg);
          clearAccessToken();
          setAccessTokenState(null);
          setAdmin(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  function onLogout() {
    clearAccessToken();
    setAccessTokenState(null);
    setAdmin(null);
    navigate({ name: 'dashboard' });
  }

  function onLoggedIn(token: string) {
    setAccessToken(token);
    setAccessTokenState(token);
    // If user arrived via a deep link, stay on that page after login
    const current = viewFromHash(window.location.hash);
    if (current.name !== 'dashboard') {
      navigate(current);
    } else {
      navigate({ name: 'dashboard' });
    }
  }

  const isAuthed = !!accessToken;

  // Role-based access: editors are the "employee" role and only see
  // content sections + their own account. Anything tied to user
  // management, reports, or admin CRUD is hidden and the matching
  // hash URL falls back to the dashboard.
  const role = (admin?.role ?? '').toLowerCase();
  const isPrivileged = role === 'admin' || role === 'superadmin';
  const isSuperadmin = role === 'superadmin';

  // Moderation backlog badge. Re-read on every navigation so the count
  // settles right after the admin clears part of the queue; the
  // endpoint is a single aggregate query, so this is cheap.
  useEffect(() => {
    if (!accessToken || !isPrivileged) {
      setPendingComments(0);
      return;
    }
    let cancelled = false;
    commentCounts(accessToken)
      .then((c) => {
        if (!cancelled) setPendingComments(c.pending);
      })
      .catch(() => {
        // A failing badge must never break navigation.
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, isPrivileged, view]);

  const allowedViews = new Set<View['name']>([
    'dashboard',
    'books',
    'books:add',
    'books:edit',
    'taxonomy',
    'categories',
    'uploads',
    'account',
  ]);
  if (isPrivileged) {
    allowedViews.add('comments');
    allowedViews.add('users');
    allowedViews.add('reports');
    allowedViews.add('activity');
  }
  if (isSuperadmin) {
    allowedViews.add('admins');
  }
  const effectiveView: View = allowedViews.has(view.name) ? view : { name: 'dashboard' };

  return (
    <div className="appFrame">
      <div className="topbar">
        <div className="topbarInner">
          <div className="topbarLeft">
            {accessToken ? (
              <button
                className="btn navToggle"
                type="button"
                onClick={() => setNavOpen((v) => !v)}
                aria-expanded={navOpen}
                aria-controls="sidenav"
              >
                {navOpen ? 'Close' : 'Menu'}
              </button>
            ) : null}

            <div className="brandRow" style={{ minWidth: 0 }}>
              <img className="brandLogo" src="/brand/full_logo.svg" alt="Unplayist" />
              <span className="pill">
                <span className="muted">Admin Panel</span>
              </span>
            </div>
          </div>

          <div className="topbarRight">
            {isAuthed ? (
              <>
                <span className="pill">
                  <span className="muted">Admin</span>
                  <span>{admin?.email ?? '...'}</span>
                  {admin?.role ? <span className="muted">({admin.role})</span> : null}
                </span>
                <button className="btn" onClick={onLogout}>
                  Logout
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {!accessToken ? (
        <div className="container">
          {adminError ? (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="errorBox">{adminError}</div>
            </div>
          ) : null}
          <LoginView onLoggedIn={onLoggedIn} />
        </div>
      ) : !admin ? (
        <div className="container">
          <div className="card">
            <div className="sectionTitle">Loading session…</div>
            <div className="muted">Fetching admin profile.</div>
          </div>
        </div>
      ) : (
        <div className="layout">
          <aside id="sidenav" className={`sidenav ${navOpen ? '' : 'sidenavClosed'}`} aria-label="Primary navigation">
            <div className="sidenavHeader">
              <div>
                <div className="sidenavTitle">Navigation</div>
                <div className="sidenavHint">Fast access to core areas</div>
              </div>
              <button className="btn navToggle" type="button" onClick={() => setNavOpen(false)}>
                Close
              </button>
            </div>

            <NavItem label="Dashboard" hash="#dashboard" active={effectiveView.name === 'dashboard'} onNav={navigate} setNavOpen={setNavOpen} />
            <NavItem label="Books" hash="#books" active={effectiveView.name === 'books' || effectiveView.name === 'books:add' || effectiveView.name === 'books:edit'} onNav={navigate} setNavOpen={setNavOpen} />
            <NavItem label="Taxonomy" hash="#taxonomy" active={effectiveView.name === 'taxonomy'} onNav={navigate} setNavOpen={setNavOpen} />
            <NavItem label="Categories (legacy)" hash="#categories" active={effectiveView.name === 'categories'} onNav={navigate} setNavOpen={setNavOpen} />
            <NavItem label="Uploads" hash="#uploads" active={effectiveView.name === 'uploads'} onNav={navigate} setNavOpen={setNavOpen} />
            {isPrivileged ? (
              <>
                <NavItem label="Comments" hash="#comments" active={effectiveView.name === 'comments'} onNav={navigate} setNavOpen={setNavOpen} badge={pendingComments} />
                <NavItem label="Users" hash="#users" active={effectiveView.name === 'users'} onNav={navigate} setNavOpen={setNavOpen} />
                <NavItem label="Reports" hash="#reports" active={effectiveView.name === 'reports'} onNav={navigate} setNavOpen={setNavOpen} />
                <NavItem label="Activity Logs" hash="#activity" active={effectiveView.name === 'activity'} onNav={navigate} setNavOpen={setNavOpen} />
              </>
            ) : null}
            {isSuperadmin ? (
              <NavItem label="Admins" hash="#admins" active={effectiveView.name === 'admins'} onNav={navigate} setNavOpen={setNavOpen} />
            ) : null}
            <div className="divider" />
            <NavItem label="Account" hash="#account" active={effectiveView.name === 'account'} onNav={navigate} setNavOpen={setNavOpen} />
          </aside>

          <div className="content">
            {effectiveView.name === 'dashboard' ? (
              <DashboardPage
                accessToken={accessToken}
                onGoBooks={() => navigate({ name: 'books' })}
                onGoUsers={() => navigate({ name: 'users' })}
                onGoAdmins={() => navigate({ name: 'admins' })}
                onEditBook={(uuid) => navigate({ name: 'books:edit', uuid })}
              />
            ) : effectiveView.name === 'books' ? (
              <BooksListPage
                accessToken={accessToken}
                onAdd={() => navigate({ name: 'books:add' })}
                onEdit={(uuid) => navigate({ name: 'books:edit', uuid })}
              />
            ) : effectiveView.name === 'books:add' ? (
              <AddBookFlow accessToken={accessToken} onDone={() => navigate({ name: 'books' })} />
            ) : effectiveView.name === 'books:edit' ? (
              <EditBookFlow accessToken={accessToken} uuid={effectiveView.uuid} onDone={() => navigate({ name: 'books' })} />
            ) : effectiveView.name === 'taxonomy' ? (
              <TaxonomyPage accessToken={accessToken} />
            ) : effectiveView.name === 'categories' ? (
              <CategoriesPage accessToken={accessToken} />
            ) : effectiveView.name === 'uploads' ? (
              <UploadsPage accessToken={accessToken} />
            ) : effectiveView.name === 'comments' && isPrivileged ? (
              <CommentsPage accessToken={accessToken} />
            ) : effectiveView.name === 'users' && isPrivileged ? (
              <UsersPage accessToken={accessToken} />
            ) : effectiveView.name === 'reports' && isPrivileged ? (
              <ReportsPage accessToken={accessToken} />
            ) : effectiveView.name === 'activity' && isPrivileged ? (
              <AdminActivityPage accessToken={accessToken} />
            ) : effectiveView.name === 'admins' && isSuperadmin ? (
              <AdminsPage accessToken={accessToken} currentAdminUuid={admin.uuid} />
            ) : (
              <AccountPage
                accessToken={accessToken}
                adminEmail={admin.email}
                adminRole={admin.role}
                totpEnabled={admin.totp_enabled}
                onTotpChanged={refreshMe}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NavItem({
  label,
  hash,
  active,
  onNav,
  setNavOpen,
  badge = 0,
}: {
  label: string;
  hash: string;
  active: boolean;
  onNav: (v: View) => void;
  setNavOpen: (v: boolean) => void;
  /** Pending-work counter; hidden when zero. */
  badge?: number;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      <button
        className={`navItem ${active ? 'navItemActive' : ''}`}
        type="button"
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        onClick={() => {
          onNav(viewFromHash(hash));
          setNavOpen(false);
        }}
      >
        <span>{label}</span>
        {badge > 0 ? (
          <span
            style={{
              minWidth: 20,
              padding: '1px 6px',
              borderRadius: 999,
              background: 'var(--u-lime, #ccff00)',
              color: '#0a192f',
              fontSize: 11,
              fontWeight: 900,
              lineHeight: '18px',
              textAlign: 'center',
            }}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </button>
      <a
        href={hash}
        target="_blank"
        rel="noreferrer"
        title="Open in new tab"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          borderRadius: 8,
          color: 'rgba(10,25,47,0.35)',
          transition: 'color 0.15s, background 0.15s',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'rgba(10,25,47,0.85)';
          e.currentTarget.style.background = 'rgba(10,25,47,0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(10,25,47,0.35)';
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      </a>
    </div>
  );
}

/* ───── Hash ↔ View mapping ───── */

function viewFromHash(hash: string): View {
  const raw = hash.replace(/^#\/?/, '').trim();
  const parts = raw.split('/');
  const base = (parts[0] || '').toLowerCase();

  switch (base) {
    case 'books':
      if (parts[1] === 'add') return { name: 'books:add' };
      if (parts[1] === 'edit' && parts[2]) return { name: 'books:edit', uuid: parts[2] };
      return { name: 'books' };
    case 'taxonomy':
      return { name: 'taxonomy' };
    case 'categories':
      return { name: 'categories' };
    case 'uploads':
      return { name: 'uploads' };
    case 'comments':
      return { name: 'comments' };
    case 'users':
      return { name: 'users' };
    case 'reports':
      return { name: 'reports' };
    case 'activity':
      return { name: 'activity' };
    case 'admins':
      return { name: 'admins' };
    case 'account':
      return { name: 'account' };
    default:
      return { name: 'dashboard' };
  }
}

function viewToHash(v: View): string {
  switch (v.name) {
    case 'dashboard':
      return '#dashboard';
    case 'books':
      return '#books';
    case 'books:add':
      return '#books/add';
    case 'books:edit':
      return `#books/edit/${v.uuid}`;
    case 'taxonomy':
      return '#taxonomy';
    case 'categories':
      return '#categories';
    case 'uploads':
      return '#uploads';
    case 'comments':
      return '#comments';
    case 'users':
      return '#users';
    case 'reports':
      return '#reports';
    case 'activity':
      return '#activity';
    case 'admins':
      return '#admins';
    case 'account':
      return '#account';
  }
}

